import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { metaApi } from '../services/api';

const fmtTime = (iso) => {
  if (!iso) return 'Nunca';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// Sidebar nav, grouped by product area. `section: null` renders items with no
// group header (top/tail of the list). A group whose items are all filtered out
// by access is skipped entirely — headers never show up empty.
const NAV_GROUPS = [
  {
    section: null,
    items: [
      { path: '/', label: 'Dashboard', icon: 'dashboard' },
    ],
  },
  {
    section: 'Meta',
    items: [
      { path: '/accounts', label: 'Contas Meta', icon: 'hub' },
      { path: '/reports', label: 'Relatórios Meta', icon: 'insert_drive_file' },
      { path: '/page-reports', label: 'Relatórios Páginas', icon: 'insights' },
    ],
  },
  {
    section: 'Google Ads',
    items: [
      { path: '/google-ads-reports', label: 'Relatórios Google Ads', icon: 'ads_click' },
    ],
  },
  {
    section: 'Relatórios Combinados',
    items: [
      { path: '/combined-reports', label: 'Relatórios Combinados', icon: 'merge' },
      { path: '/franchise-reports', label: 'Relatório Franquia', icon: 'storefront' },
      { path: '/franchise-v2', label: 'Franquia V2', icon: 'merge_type' },
    ],
  },
  {
    section: 'Clínicas',
    items: [
      { path: '/clinicorp-reports', label: 'Clinicorp', icon: 'local_hospital' },
      { path: '/leads', label: 'Leads', icon: 'group' },
      { path: '/page-subscriptions', label: 'Inscrição Páginas', icon: 'rss_feed', adminOnly: true },
      { path: '/clinic-data', label: 'Dados Clínicas', icon: 'medical_information' },
      { path: '/clinic-data/reports', label: 'Relatórios', icon: 'summarize', permissionPath: '/clinic-data' },
      { path: '/clinic-data/reports?mode=funnel', label: 'Funil Projetado', icon: 'filter_alt', permissionPath: '/clinic-data' },
      { path: '/lead-source-mappings', label: 'Mapeamento Domínios', icon: 'alt_route', adminOnly: true },
    ],
  },
  {
    section: null,
    items: [
      { path: '/conversions', label: 'Conversões', icon: 'track_changes' },
      { path: '/forms', label: 'Formulários', icon: 'dynamic_form' },
    ],
  },
];

const Layout = ({ children }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const [syncOpen, setSyncOpen] = useState(() => {
    try { return localStorage.getItem('syncWidgetOpen') !== 'false'; } catch { return false; }
  });
  const [syncStatus, setSyncStatus] = useState({ pagesLastAt: null, accountsLastAt: null });
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebarCollapsedGroups') || '{}'); } catch { return {}; }
  });

  const toggleGroup = (section) => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [section]: !prev[section] };
      try { localStorage.setItem('sidebarCollapsedGroups', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  useEffect(() => {
    metaApi.getSyncStatus()
      .then(res => setSyncStatus(res.data || {}))
      .catch(() => {});
  }, []);

  const toggleSync = () => {
    setSyncOpen(prev => {
      const next = !prev;
      try { localStorage.setItem('syncWidgetOpen', String(next)); } catch {}
      return next;
    });
  };

  const isActive = (path) => {
    const [p, q] = path.split('?');
    if (location.pathname !== p) return false;
    if (q) return location.search.includes(q);
    return !location.search.includes('mode=funnel');
  };

  const hasAccess = (path) => {
    if (!user) return false;
    if (user.roleType === 'ADMIN') return true;
    const basePaths = ['/', '/accounts', '/leads', '/settings'];
    if (basePaths.includes(path)) return true;
    if (user.accessibleReports && user.accessibleReports.includes(path)) return true;
    return false;
  };

  // Same access rule as hasAccess(), plus adminOnly and permissionPath (used when
  // a sub-page like /clinic-data/reports reuses its parent's tab grant).
  const canSeeItem = (item) => {
    if (item.adminOnly) return user?.roleType === 'ADMIN';
    return hasAccess(item.permissionPath || item.path.split('?')[0]);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-surface font-body text-on-surface">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col h-full py-8 px-6 bg-sidebar border-r border-sidebar-border shrink-0 w-64">
        <div className="mb-10 px-2 flex justify-center w-full">
          <img src="/LOGO_dark.png" alt="Lumina Reporting" className="h-9 w-auto" />
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(148,163,184,0.4)_transparent]">
          {NAV_GROUPS.map((group, gi) => {
            const visibleItems = group.items.filter(canSeeItem);
            if (visibleItems.length === 0) return null;
            const hasActiveItem = visibleItems.some(item => isActive(item.path));
            // A group stays open if it holds the current page, even if the user
            // collapsed it earlier — never hide where you already are.
            const isCollapsed = !!collapsedGroups[group.section] && !hasActiveItem;
            return (
              <div key={group.section || `top-${gi}`} className={gi > 0 ? 'pt-3' : ''}>
                {group.section && (
                  <button
                    onClick={() => toggleGroup(group.section)}
                    className="w-full flex items-center justify-between px-3 pb-1.5 text-[9px] font-bold uppercase tracking-widest text-sidebar-text/40 hover:text-sidebar-text/70 transition-colors"
                  >
                    {group.section}
                    <span className="material-symbols-outlined text-[13px] transition-transform" style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                      expand_more
                    </span>
                  </button>
                )}
                {!isCollapsed && (
                  <div className="space-y-1">
                    {visibleItems.map(item => (
                      <Link key={item.path} to={item.path} className={`sidebar-item ${isActive(item.path) ? 'sidebar-item-active' : 'sidebar-item-inactive'}`}>
                        <span className="material-symbols-outlined shrink-0" data-icon={item.icon}>{item.icon}</span>
                        <span className="font-label uppercase tracking-wide text-[9px] leading-tight">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="mt-auto space-y-2 border-t border-sidebar-border pt-4">

          {/* Sync Status Widget — collapsible */}
          <div className="rounded-lg border border-sidebar-border overflow-hidden">
            <button
              onClick={toggleSync}
              className="w-full flex items-center justify-between px-3 py-2 text-sidebar-text hover:bg-sidebar-hover transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[16px] text-sidebar-text">sync</span>
                <span className="font-label uppercase tracking-wider text-[9px] text-sidebar-text">Última Sync API</span>
              </div>
              <span className="material-symbols-outlined text-[14px] text-sidebar-text transition-transform" style={{ transform: syncOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                expand_more
              </span>
            </button>

            {syncOpen && (
              <div className="px-3 pb-3 pt-1 bg-sidebar-hover space-y-2">
                <div>
                  <p className="text-[8px] font-bold uppercase tracking-wider text-sidebar-text/70 mb-0.5">Páginas (owned/client)</p>
                  <p className="text-[10px] font-semibold text-slate-200">{fmtTime(syncStatus.pagesLastAt)}</p>
                </div>
                <div>
                  <p className="text-[8px] font-bold uppercase tracking-wider text-sidebar-text/70 mb-0.5">Contas de Anúncio</p>
                  <p className="text-[10px] font-semibold text-slate-200">{fmtTime(syncStatus.accountsLastAt)}</p>
                </div>
                <p className="text-[8px] text-sidebar-text/70 leading-tight mt-1">Sync auto: páginas 1×/hora, contas 1×/dia às 01h</p>
              </div>
            )}
          </div>

          <Link
            to="/settings"
            className={`sidebar-item ${isActive('/settings') ? 'sidebar-item-active' : 'sidebar-item-inactive'}`}
          >
            <span className="material-symbols-outlined shrink-0" data-icon="settings">settings</span>
            <span className="font-label uppercase tracking-wide text-[9px] leading-tight">Configurações</span>
          </Link>

          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sidebar-text font-medium hover:text-red-400 hover:bg-sidebar-hover transition-colors duration-200 mt-2"
          >
            <span className="material-symbols-outlined shrink-0" data-icon="logout">logout</span>
            <span className="font-label uppercase tracking-wide text-[9px] leading-tight">Sair</span>
          </button>
        </div>
      </aside>

      {/* Main Canvas */}
      <main className="flex-1 flex flex-col overflow-y-auto bg-surface relative">
        <header className="sticky top-0 z-50 glass-header flex justify-between items-center w-full px-8 md:px-12 py-4 shadow-sm shadow-slate-200/50">
          <div className="flex items-center flex-1 max-w-xl">
            <div className="relative w-full hidden md:block">
              <span className="material-symbols-outlined absolute left-4 top-1/2 -transition-translate-y-1/2 text-slate-400" data-icon="search" style={{ transform: "translateY(-50%)" }}>search</span>
              <input className="input pl-12" placeholder="Buscar informações..." type="text" />
            </div>
          </div>

          <div className="flex items-center gap-4 ml-8">
            {user && (
              <div className="flex items-center gap-3 px-2">
                <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">
                  {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="overflow-hidden hidden md:block">
                  <p className="text-[10px] font-bold text-slate-800 truncate">{user?.username || 'Usuário'}</p>
                  <p className="text-[8px] text-slate-500 truncate">{user.roleType === 'ADMIN' ? 'Admin' : 'Usuário'}</p>
                </div>
              </div>
            )}
          </div>
        </header>

        <div className="px-8 md:px-12 py-10 max-w-[1400px] mx-auto w-full">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-6 py-3 flex justify-between items-center z-50 overflow-x-auto gap-4">
        <Link className={`flex flex-col items-center gap-1 min-w-max ${isActive('/') ? 'text-primary' : 'text-slate-400'}`} to="/">
          <span className="material-symbols-outlined">dashboard</span>
          <span className="text-[10px] font-bold">Início</span>
        </Link>
        {hasAccess('/accounts') && (
          <Link className={`flex flex-col items-center gap-1 min-w-max ${isActive('/accounts') ? 'text-primary' : 'text-slate-400'}`} to="/accounts">
            <span className="material-symbols-outlined">hub</span>
            <span className="text-[10px]">Contas</span>
          </Link>
        )}
        {hasAccess('/reports') && (
          <Link className={`flex flex-col items-center gap-1 min-w-max ${isActive('/reports') ? 'text-primary' : 'text-slate-400'}`} to="/reports">
            <span className="material-symbols-outlined">insert_drive_file</span>
            <span className="text-[10px]">Rel. Meta</span>
          </Link>
        )}
        {hasAccess('/page-reports') && (
          <Link className={`flex flex-col items-center gap-1 min-w-max ${isActive('/page-reports') ? 'text-primary' : 'text-slate-400'}`} to="/page-reports">
            <span className="material-symbols-outlined">insights</span>
            <span className="text-[10px]">Páginas</span>
          </Link>
        )}
        {hasAccess('/franchise-reports') && (
          <Link className={`flex flex-col items-center gap-1 min-w-max ${isActive('/franchise-reports') ? 'text-primary' : 'text-slate-400'}`} to="/franchise-reports">
            <span className="material-symbols-outlined">storefront</span>
            <span className="text-[10px]">Franquia</span>
          </Link>
        )}
        {hasAccess('/google-ads-reports') && (
          <Link className={`flex flex-col items-center gap-1 min-w-max ${isActive('/google-ads-reports') ? 'text-primary' : 'text-slate-400'}`} to="/google-ads-reports">
            <span className="material-symbols-outlined">ads_click</span>
            <span className="text-[10px]">Google Ads</span>
          </Link>
        )}
        {hasAccess('/combined-reports') && (
          <Link className={`flex flex-col items-center gap-1 min-w-max ${isActive('/combined-reports') ? 'text-primary' : 'text-slate-400'}`} to="/combined-reports">
            <span className="material-symbols-outlined">merge</span>
            <span className="text-[10px]">Combinado</span>
          </Link>
        )}
        {hasAccess('/clinicorp-reports') && (
          <Link className={`flex flex-col items-center gap-1 min-w-max ${isActive('/clinicorp-reports') ? 'text-primary' : 'text-slate-400'}`} to="/clinicorp-reports">
            <span className="material-symbols-outlined">local_hospital</span>
            <span className="text-[10px]">Clinicorp</span>
          </Link>
        )}
        {hasAccess('/franchise-v2') && (
          <Link className={`flex flex-col items-center gap-1 min-w-max ${isActive('/franchise-v2') ? 'text-primary' : 'text-slate-400'}`} to="/franchise-v2">
            <span className="material-symbols-outlined">merge_type</span>
            <span className="text-[10px]">Franquia V2</span>
          </Link>
        )}
        {hasAccess('/leads') && (
          <Link className={`flex flex-col items-center gap-1 min-w-max ${isActive('/leads') ? 'text-primary' : 'text-slate-400'}`} to="/leads">
            <span className="material-symbols-outlined">group</span>
            <span className="text-[10px]">Leads</span>
          </Link>
        )}
        {hasAccess('/conversions') && (
          <Link className={`flex flex-col items-center gap-1 min-w-max ${isActive('/conversions') ? 'text-primary' : 'text-slate-400'}`} to="/conversions">
            <span className="material-symbols-outlined">track_changes</span>
            <span className="text-[10px]">Conversões</span>
          </Link>
        )}
        {hasAccess('/forms') && (
          <Link className={`flex flex-col items-center gap-1 min-w-max ${location.pathname.startsWith('/forms') ? 'text-primary' : 'text-slate-400'}`} to="/forms">
            <span className="material-symbols-outlined">dynamic_form</span>
            <span className="text-[10px]">Forms</span>
          </Link>
        )}
        {hasAccess('/settings') && (
          <Link className={`flex flex-col items-center gap-1 min-w-max ${isActive('/settings') ? 'text-primary' : 'text-slate-400'}`} to="/settings">
            <span className="material-symbols-outlined">settings</span>
            <span className="text-[10px]">Ajustes</span>
          </Link>
        )}
      </nav>
    </div>
  );
};

export default Layout;
